import * as React from "react";
import { useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import type { INavItem } from "src/components/layouts";

export interface IAppNavigationContext {
    isOpen: boolean;
    isDesktop: boolean;
    drawerItems: INavItem[];
    breadcrumbs: INavItem[];
    setBreadcrumbs(items: INavItem[]): void;
    clearBreadcrumbs(): void;
    setDrawerItems(items: INavItem[]): void;
    toggleDrawer(isOpen: boolean): void;
    closeMobileDrawer(): void;
}

const AppNavigationContext = React.createContext<
    IAppNavigationContext | undefined
>(undefined);

export const AppNavigationProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const [desktopOpen, setDesktopOpen] = useState(true);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [media] = useState(() => window.matchMedia("(min-width: 992px)"));
    const subscribe = React.useCallback((notify: () => void) => {
        const changed = () => {
            if (!media.matches) setMobileOpen(false);
            notify();
        };
        media.addEventListener("change", changed);
        return () => media.removeEventListener("change", changed);
    }, [media]);
    const isDesktop = useSyncExternalStore(subscribe, () => media.matches);
    const [drawerItems, setDrawerItems] = useState<INavItem[]>([]);
    const [breadcrumbs, setBreadcrumbs] = useState<INavItem[]>([]);
    const activeLayout = useRef(isDesktop);
    activeLayout.current = isDesktop;
    const isOpen = isDesktop ? desktopOpen : mobileOpen && drawerItems.length > 0;

    useEffect(() => {
        if (drawerItems.length === 0) setMobileOpen(false);
    }, [drawerItems.length, mobileOpen]);

    const actions = useMemo(
        () => ({
            setBreadcrumbs: (items: INavItem[]) => {
                setBreadcrumbs(items);
            },
            clearBreadcrumbs: () => {
                setBreadcrumbs([]);
            },
            setDrawerItems: (items: INavItem[]) => {
                setDrawerItems(items);
                if (items.length === 0) setMobileOpen(false);
            },
            toggleDrawer: (open: boolean) => {
                if (activeLayout.current) setDesktopOpen(open);
                else setMobileOpen(open);
            },
            closeMobileDrawer: () => setMobileOpen(false),
        }),
        [],
    );

    const value = useMemo<IAppNavigationContext>(
        () => ({
            isOpen,
            isDesktop,
            drawerItems,
            breadcrumbs,
            ...actions,
        }),
        [
            isOpen,
            isDesktop,
            drawerItems,
            breadcrumbs,
            actions,
        ],
    );

    return (
        <AppNavigationContext.Provider value={value}>
            {children}
        </AppNavigationContext.Provider>
    );
};

export const useAppNavigation = (): IAppNavigationContext => {
    const context = useContext(AppNavigationContext);

    if (!context) {
        throw new Error(
            "useAppNavigation must be used within AppNavigationProvider",
        );
    }

    return context;
};

export default AppNavigationContext;
